// `GET /v1/agents/queue` (#6210) — one agent's live tasks, a page of rows, and for the
// escalation agent its STUCK PRs leading the first page (#6338 wave 2). The item and the
// state it carries are `value-objects/agent-queue.ts`; this is its published projection,
// shared by the route and the dashboard client so neither holds a mirror.
//
// A UNION, because the two arms are different things: a task row is a projection of
// `tasks`, a stuck row is a projection of a PULL REQUEST (`stuckPrsOf`,
// `value-objects/stuck-pr.ts`) that carries no task identity at all — `repo` + PR number
// is its identity. Nullable task fields would let one arm parse as the other, so the
// schema is two strict objects and `state` is the discriminant.
//
// STRICT. The read is `read:tasks`, and a row is a projection of `tasks` or of the stuck
// read, never the stored row: the envelope (a problem statement, a sink, a BYOK pool key
// on `llm`) has no key here, so a serializer that spread more of the row is a parse
// failure at the seam rather than a field nobody noticed.

import { z } from 'zod';
import { EscalationDeliveryStateSchema } from '../entities/escalation';
import { TaskHoldReasonSchema, TaskStatusSchema } from '../entities/task';
import {
  AGENT_QUEUE_TASK_STATES,
  type AgentQueueItem,
  type AgentQueueTaskState,
} from '../value-objects/agent-queue';
import { STUCK_PR_PARK_CLASSES, type StuckPrIdleBasis } from '../value-objects/stuck-pr';
import { TaskOriginSchema } from '../value-objects/task-envelope';
import { defineResource } from './resource';

/** The TASK arm — a live task the agent holds. */
const AgentQueueTaskWireSchema = z
  .object({
    id: z.string().describe('The task id (`tsk_…`).'),
    state: z
      .enum(AGENT_QUEUE_TASK_STATES as [AgentQueueTaskState, ...AgentQueueTaskState[]])
      .describe(
        'Where the task sits: `queued` (pending: waiting for a box, or held), `running` (on a box now), `watching` (its PR is open and the babysit watch follows it to a merge).',
      ),
    status: TaskStatusSchema.describe(
      'The fine task status behind `state`: pending, assigned, in_progress, awaiting_ci, awaiting_review or mergeable.',
    ),
    origin: TaskOriginSchema.describe('Where the task came from; the roster maps it to the agent.'),
    repo_full_name: z.string().describe('The repository, `owner/name`.'),
    pr_number: z
      .number()
      .int()
      .positive()
      .nullable()
      .describe(
        'The pull request the task is about: a review, babysit or reply round names its PR, a coding task the PR it opened. Null when it has none yet.',
      ),
    title: z.string().describe('The task title.'),
    hold_reason: TaskHoldReasonSchema.nullable().describe(
      'Why a queued task is held rather than merely waiting its turn; null when nothing holds it.',
    ),
    hold_until_iso: z
      .string()
      .datetime()
      .nullable()
      .describe(
        'When a timed hold lapses (ISO 8601); null for no hold, or a hold that lifts on an event rather than a clock.',
      ),
    runner_id: z
      .string()
      .nullable()
      .describe('The box that claimed the task; null until one does.'),
    run_id: z
      .string()
      .nullable()
      .describe('The run the box is executing, the key of its run timeline; null until it starts.'),
    created_at_iso: z.string().datetime().describe('When the task was minted (ISO 8601).'),
    updated_at_iso: z
      .string()
      .datetime()
      .describe(
        'When the task last changed (ISO 8601). Task rows are ordered on it, newest first; stuck rows lead the escalation queue’s first page, longest idle first.',
      ),
  })
  .strict();

/**
 * The rescue leg of a stuck row, as the wire answers it — `none` carries no `reason`,
 * `spent` carries the round's `outcome`, and neither key ever rides the other's arm.
 */
const AgentQueueRescueWireSchema = z.union([
  z.object({ state: z.literal('none') }).strict(),
  z
    .object({
      state: z.literal('live'),
      reason: z.string().describe('The park the in-flight rescue round answers.'),
    })
    .strict(),
  z
    .object({
      state: z.literal('spent'),
      reason: z.string().describe('The park the newest rescue round answered.'),
      outcome: z.string().describe('How it ended: its failure kind, else its task status.'),
    })
    .strict(),
]);

/** The STUCK arm — a parked AI-managed PR on the escalation queue's first page (#6338). */
const AgentQueueStuckWireSchema = z
  .object({
    state: z
      .literal('stuck')
      .describe(
        'Escalation only, first page: an open AI-managed pull request idle past the stuck read’s floor — parked, not a task the agent holds.',
      ),
    repo_full_name: z.string().describe('The repository, `owner/name`.'),
    pr_number: z.number().int().positive().describe('The stuck pull request.'),
    title: z.string().nullable().describe('The pull request’s title; null when unreadable.'),
    park_class: z
      .enum(STUCK_PR_PARK_CLASSES)
      .describe(
        'What the PR is stuck on: coding, review, in_flight, ci_infra, human_hold, external_hold, github_only, repo_config, platform_fault, not_ai_managed, gate_edit_owner_applies (a gate-path edit of our own PR that the owner applies), unobserved (babysit never saw it), unobserved_head (no hop saw the current head), waiting (nothing names it yet), or unclassified (an escalation reason the tables do not know).',
      ),
    park_reason: z
      .string()
      .nullable()
      .describe(
        'The stored escalation reason the park cites, when it cites one; null when the park is not reason-backed.',
      ),
    blocker: z
      .string()
      .nullable()
      .describe(
        'A machine-readable token naming the blocker (`escalation:<reason>`, `merge_decision:<stamp>`, `merge_block:<state>`, …); null when nothing names it. Branch on `park_class`, never parse this.',
      ),
    idle_since_iso: z
      .string()
      .datetime()
      .nullable()
      .describe(
        'When the PR’s idle clock started (ISO 8601): the first babysit hop that observed the current head, anchored at the maintainer’s re-arm when later. Null when no clock could be computed.',
      ),
    idle_basis: z
      .enum(['head_first_seen', 'unobserved_head', 'unobserved'] as [
        StuckPrIdleBasis,
        ...StuckPrIdleBasis[],
      ])
      .describe(
        'Which clock `idle_since_iso` is: `head_first_seen` (a hop observed the current head), `unobserved_head` (hops exist, none on the current head) or `unobserved` (no hop at all — the PR was opened or re-armed, never watched).',
      ),
    opened_at_iso: z
      .string()
      .datetime()
      .nullable()
      .describe('When the PR was opened (ISO 8601); null when the projection has not written one.'),
    last_action: z
      .object({
        executed: z
          .string()
          .describe('What the newest babysit hop executed (`escalated`, `wait`, …).'),
        at_iso: z.string().datetime().describe('When it executed (ISO 8601).'),
      })
      .strict()
      .nullable()
      .describe(
        'What the newest babysit hop DID, and when; null when the PR was never observed by babysit.',
      ),
    rescue: AgentQueueRescueWireSchema.describe(
      'The rescue leg: `none` (no rescue round for this park since the re-arm), `live` (one is in flight) or `spent` (the newest one, with how it ended).',
    ),
    escalation: z
      .object({
        reason: z
          .string()
          .describe('The structured discriminant (`payload.babysit_reason`), as stored.'),
        delivery_state: EscalationDeliveryStateSchema.describe(
          'Whether a human was reached. `collapsed` means NOBODY was emailed on this row — another copy went; `repeat` means this row is a re-fire that reached nobody.',
        ),
        created_at_iso: z
          .string()
          .datetime()
          .describe('When the escalation row was filed (ISO 8601).'),
      })
      .strict()
      .nullable()
      .describe(
        'The escalation the PR’s newest hop landed on, as stored; null when the newest hop escalated nothing.',
      ),
    rescue_due_at_iso: z
      .string()
      .datetime()
      .nullable()
      .describe(
        'When the rescue’s hand-over clock runs out (ISO 8601): `idle_since_iso` plus the rescue clock (`STUCK_PR_RESCUE_AFTER_MS`, 3h), set only for a park today’s rescue answers. Null otherwise — and for any park whose rescue cannot mint a round, a due instant would promise a hand-over that cannot happen.',
      ),
    alert_at_iso: z
      .string()
      .datetime()
      .nullable()
      .describe(
        'The earlier of 6h idle and 12h open (ISO 8601) — when the alert names this PR. Null when neither clock can run.',
      ),
  })
  .strict();

export const AgentQueueItemWireSchema = z.union([
  AgentQueueTaskWireSchema,
  AgentQueueStuckWireSchema,
]);

export type AgentQueueItemWire = z.infer<typeof AgentQueueItemWireSchema>;

/** The task arm of {@link AgentQueueItemResource.serialize} — a row the agent holds. */
function serializeTask(
  r: Extract<AgentQueueItem, { id: string }>,
): z.infer<typeof AgentQueueTaskWireSchema> {
  return {
    id: r.id,
    state: r.state,
    status: r.status,
    origin: r.origin,
    repo_full_name: r.repoFullName,
    pr_number: r.prNumber,
    title: r.title,
    hold_reason: r.holdReason,
    hold_until_iso: r.holdUntil === null ? null : r.holdUntil.toISOString(),
    runner_id: r.runnerId,
    run_id: r.runId,
    created_at_iso: r.createdAt.toISOString(),
    updated_at_iso: r.updatedAt.toISOString(),
  };
}

/** The stuck arm of {@link AgentQueueItemResource.serialize} — a parked PR (#6338). */
function serializeStuck(
  r: Extract<AgentQueueItem, { park: unknown }>,
): z.infer<typeof AgentQueueStuckWireSchema> {
  return {
    state: r.state,
    repo_full_name: r.repoFullName,
    pr_number: r.prNumber,
    title: r.title,
    park_class: r.park.parkClass,
    park_reason: r.park.reason,
    blocker: r.park.blocker,
    idle_since_iso: r.idleSince === null ? null : r.idleSince.toISOString(),
    idle_basis: r.idleBasis,
    opened_at_iso: r.openedAt === null ? null : r.openedAt.toISOString(),
    last_action:
      r.lastAction === null
        ? null
        : { executed: r.lastAction.executed, at_iso: r.lastAction.at.toISOString() },
    rescue:
      r.rescue.kind === 'none'
        ? { state: 'none' }
        : r.rescue.kind === 'live'
          ? { state: 'live', reason: r.rescue.reason }
          : { state: 'spent', reason: r.rescue.reason, outcome: r.rescue.outcome },
    escalation:
      r.escalation === null
        ? null
        : {
            reason: r.escalation.reason,
            delivery_state: r.escalation.deliveryState,
            created_at_iso: r.escalation.createdAt.toISOString(),
          },
    rescue_due_at_iso: r.rescueDueAt === null ? null : r.rescueDueAt.toISOString(),
    alert_at_iso: r.alertAt === null ? null : r.alertAt.toISOString(),
  };
}

/** One row of `GET /v1/agents/queue`, as it pages it (`Page<AgentQueueItem>`). */
export const AgentQueueItemResource = defineResource<
  AgentQueueItem,
  typeof AgentQueueItemWireSchema
>({
  schema: AgentQueueItemWireSchema,
  serialize: (r) => ('park' in r ? serializeStuck(r) : serializeTask(r)),
});
